import { useQueryClient } from '@tanstack/react-query';
import { AnimatePresence } from 'framer-motion';
import { forwardRef, useState } from 'react';
import styled from 'styled-components';

import { ReactComponent as VideoIcon } from '@/assets/icons/video-placeholder.svg';
import PulsingLoader from '@/components/PulsingLoader';
import Text from '@/components/Text';
import useAlerts from '@/hooks/useAlerts';
import useToasts from '@/hooks/useToasts';
import { SubtitlesGenerationProject } from '@/types/resources';
import { api } from '@/utils/api';
import { SubsGenerationProjectVideoStatus } from '@/utils/enums';

import ApprovementModal from './ApprovementModal';
import Card, { ActionButton } from './Card';

const Video = styled.video`
  width: 100%;
  height: auto;
  max-height: 800px;

  /* &::-webkit-media-controls-panel {
    background-image: linear-gradient(transparent, transparent) !important;
  } */
`;

const Actions = styled.div`
  display: flex;
  margin-top: 32px;
  justify-content: flex-end;
  gap: 16px;
  margin-bottom: 16px;
`;

const Button = styled(ActionButton)`
  height: 56px;
  padding: 0 32px;
  font-size: 16px;
`;

const NoFilePlaceholder = styled(Text).attrs({
  $type: 'smallBold',
  $color: 'gray',
})`
  display: flex;
  flex: 1;
  padding: 16px 0;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

const LoaderHolder = styled.div`
  text-align: center;
  margin: 120px 0 124px;
`;

const LoaderTextHolder = styled.div`
  margin: 0 auto;
`;

const StyledText = styled(Text)`
  margin-top: 20px;
  margin-bottom: 6px;
`;

interface Props {
  project: SubtitlesGenerationProject;
}

const PreviewCard = forwardRef<HTMLDivElement, Props>(({ project }, ref) => {
  const alerts = useAlerts();
  const toasts = useToasts();
  const queryClient = useQueryClient();
  const [showApprovementModal, setShowApprovementModal] = useState(false);

  const onApprove = async (
    shouldCreateRelease: boolean,
    shouldMoveToResearchTool: boolean,
  ) => {
    setShowApprovementModal(false);

    const toastId = toasts.loading();

    try {
      if (
        shouldCreateRelease &&
        (!project.title || !project.productionTeam || !project.projectId)
      ) {
        toasts.close(toastId);
        alerts.error(
          'Some fields as missing!',
          "Make sure you've filled in title, production team and project id!",
        );
        return;
      }

      const { error } = await api.post<object, { error?: string }>(
        `/v1/distribution/subtitles-generation-projects/${project.id}/approve`,
        {
          should_create_release: shouldCreateRelease,
          should_move_to_research_tool: shouldMoveToResearchTool,
        },
      );

      if (error) {
        alerts.error(error);
        toasts.close(toastId);

        return;
      }

      await queryClient.invalidateQueries(['subtitles_generation_projects']);

      alerts.success('Final video successfully approved');
    } catch (_) {
      alerts.error();
    } finally {
      toasts.close(toastId);
    }
  };

  const onDownload = async () => {
    try {
      const confirmed = await alerts.confirm(
        'Would you like to move this project to "Done" tab?',
      );

      if (!confirmed) {
        return;
      }

      await api.post(
        `/v1/distribution/subtitles-generation-projects/${project.id}/approve`,
        {
          should_create_release: false,
        },
      );

      await queryClient.invalidateQueries(['subtitles_generation_projects']);

      alerts.success('Final video successfully marked as Done');
    } catch (_) {
      alerts.error();
    }
  };

  const isActionsDisabled =
    project.status === 'done' ||
    project.videoStatus === SubsGenerationProjectVideoStatus.GENERATING;

  return (
    <div ref={ref}>
      <Card
        storageKey={`subtitles_generation_project_${project.id}_preview_visibility`}
        title="Preview & Confirm"
        defaultIsOpened
        description="Preview the merged video and subtitles to approve them for distribution"
      >
        {project.videoStatus === SubsGenerationProjectVideoStatus.GENERATING ? (
          <LoaderHolder>
            <PulsingLoader />
            <LoaderTextHolder>
              <StyledText $type="button">
                Generating video with subtitles ...
              </StyledText>
              <Text $color="gray" $type="captionSemibold">
                Please be patient, this action may take
                <br />a little longer in some cases
              </Text>
            </LoaderTextHolder>
          </LoaderHolder>
        ) : project.finalVideoUrl ? (
          <>
            <Video controls key={project.finalVideoUrl}>
              <source src={project.finalVideoUrl} type="video/mp4" />
              Your browser does not support the video tag.
            </Video>
            <Actions>
              <Button
                onClick={() => setShowApprovementModal(true)}
                disabled={isActionsDisabled}
              >
                Approve
              </Button>
              <Button
                $secondary
                as="a"
                href={project.finalVideoUrl}
                rel="noreferrer"
                target="_blank"
                onClick={onDownload}
              >
                Download
              </Button>
            </Actions>
          </>
        ) : (
          <NoFilePlaceholder>
            <VideoIcon />
            <span>Final video is not generated yet</span>
          </NoFilePlaceholder>
        )}
      </Card>
      <AnimatePresence>
        {showApprovementModal && (
          <ApprovementModal
            onClose={() => setShowApprovementModal(false)}
            onApprove={onApprove}
          />
        )}
      </AnimatePresence>
    </div>
  );
});

export default PreviewCard;
